import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";

import ICONS from "../icons.js";

export const HEIF_EXTENSIONS = ["heic", "heif"];
export const IMAGE_EXTENSIONS = [
  "avif",
  "bmp",
  "gif",
  ...HEIF_EXTENSIONS,
  "jpeg",
  "jpg",
  "png",
  "svg",
  "webp",
];
export const IMAGE_ACCEPT =
  ".jpg, .jpeg, .png, .gif, .bmp, .webp, .heic, .heif, image/*";

export const FILE_FIELDS = {
  toSave: null,
  modified: null,
  size: 0,
  type: "",
  name: "",
  extension: "",
  url: "",
};
deepFreeze(FILE_FIELDS);

export default class FileRef extends DataStructure {
  constructor(data = {}) {
    data = sanitize(data, FILE_FIELDS);

    if (data.toSave) {
      const file = data.toSave;
      data.name = file.name;
      data.modified = file.lastModified
        ? new Date(file.lastModified)
        : new Date();
      data.size = file.size;
      data.type = file.type;
    } else {
      data.modified = timestampToDate(data.modified);
    }

    if (!data.extension) {
      data.extension = data.name
        .slice(data.name.lastIndexOf(".") + 1)
        .toLowerCase();
    }

    super(data);
  }

  get icon() {
    return FileRef.getIcon(this.data.extension);
  }

  static getIcon(extension) {
    const icon = "file_" + extension;
    return icon in ICONS ? icon : "file";
  }

  static extension(file = {}) {
    if (file.extension) {
      return String(file.extension).toLowerCase();
    }

    const name = file.name ? String(file.name) : "";
    const index = name.lastIndexOf(".");
    if (index >= 0 && index < name.length - 1) {
      return name.slice(index + 1).toLowerCase();
    }

    return "";
  }

  static isHeif(file = {}) {
    const extension = FileRef.extension(file);
    if (HEIF_EXTENSIONS.includes(extension)) {
      return true;
    }

    const type = file.type ? String(file.type).toLowerCase() : "";
    return [
      "image/heic",
      "image/heif",
      "image/heic-sequence",
      "image/heif-sequence",
    ].includes(type);
  }

  static isImage(file = {}) {
    const type = file.type ? String(file.type).toLowerCase() : "";
    if (type.startsWith("image/")) {
      return true;
    }

    return IMAGE_EXTENSIONS.includes(FileRef.extension(file));
  }

  static canPreviewAsImage(file = {}) {
    return FileRef.isImage(file) && !FileRef.isHeif(file);
  }

  get size() {
    return FileRef.getSize(this.data.size);
  }

  static getSize(size) {
    const i = size ? Math.floor(Math.log(size) / Math.log(1024)) : 0;
    return (
      Number((size / Math.pow(1024, i)).toFixed(2)) +
      " " +
      ["B", "kB", "MB", "GB", "TB"][i]
    );
  }
}
